import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { can, type Member, STAFF_ROLES, type StaffRole, uuidv7 } from "@luckymenu/core";

/**
 * More > Staff, shared by both apps: members, open invites, and what the
 * caller may do. The rules mirror the server (auth-tenancy.md, Managing
 * members); the server still checks every call.
 */

/** Roles the caller may give in an invite or a role change. Managers cannot make owners. */
export function assignableRoles(callerRole: StaffRole): StaffRole[] {
	if (!can(callerRole, "staff.manage")) return [];
	return STAFF_ROLES.filter((r) => callerRole === "owner" || r !== "owner");
}

/** Whether the caller may change this member at all. Managers cannot change owners. */
export function canEditMember(callerRole: StaffRole, member: Member): boolean {
	return can(callerRole, "staff.manage") && (callerRole === "owner" || member.role !== "owner");
}

/** Why a staff change was refused, for a message. */
export type StaffProblem = "last_owner" | "owner_only" | "forbidden" | "network" | "failed";

export function staffProblemOf(error: unknown): StaffProblem {
	const reason = errorDetailsOf(error).reason;
	if (reason === "last_owner" || reason === "owner_only") return reason;
	const code = errorCodeOf(error);
	if (code === "FORBIDDEN") return "forbidden";
	if (code === "NETWORK") return "network";
	return "failed";
}

/** A WhatsApp link that opens a chat picker with `text` filled in. */
export function whatsAppUrl(text: string): string {
	return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function useStaff() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const members = useQuery(trpc.staff.tenancy.listMembers.queryOptions());
	const invites = useQuery(trpc.staff.tenancy.listInvites.queryOptions());
	const refreshInvites = () =>
		queryClient.invalidateQueries({ queryKey: trpc.staff.tenancy.listInvites.queryKey() });

	const createInvite = useMutation(
		trpc.staff.tenancy.createInvite.mutationOptions({ onSuccess: refreshInvites }),
	);
	const revokeInvite = useMutation(
		trpc.staff.tenancy.revokeInvite.mutationOptions({ onSuccess: refreshInvites }),
	);
	const updateMember = useMutation(
		trpc.staff.tenancy.updateMember.mutationOptions({
			onSuccess: (member) => {
				queryClient.setQueryData(trpc.staff.tenancy.listMembers.queryKey(), (old) =>
					old?.map((m) => (m.id === member.id ? member : m)),
				);
			},
		}),
	);

	return {
		members,
		invites,
		/** A single-use invite for `role`, valid 7 days. Safe to retry: the ID is made here. */
		createInvite: (role: StaffRole) => createInvite.mutateAsync({ id: uuidv7(), role }),
		creating: createInvite.isPending,
		createProblem: createInvite.error ? staffProblemOf(createInvite.error) : null,
		revokeInvite: (inviteId: string) => revokeInvite.mutateAsync({ inviteId }),
		updateMember: (membershipId: string, change: { role?: StaffRole; status?: Member["status"] }) =>
			updateMember.mutateAsync({ membershipId, ...change }),
		updating: updateMember.isPending ? (updateMember.variables?.membershipId ?? null) : null,
		updateProblem: updateMember.error ? staffProblemOf(updateMember.error) : null,
		resetUpdate: () => updateMember.reset(),
	};
}
